import { AvailableLanguages } from '@repo/toolkit/language-types'
import { TFunction } from 'i18next'
import { z, ZodType } from 'zod'

import { DocumentStatusEnum } from '../../../../lib/types/enums/common/document-status.enum'
import { IDTypeEnum } from '../../../../lib/types/enums/common/id-type.enum'
import { AcademicZoneEnum } from '../../../../lib/types/enums/staff/academic-zone.enum'
import { DiplomaEnum } from '../../../../lib/types/enums/staff-application/diploma.enum'
import { EducationLevelEnum } from '../../../../lib/types/enums/staff-application/education-level.enum'
import { EvalutationEnum } from '../../../../lib/types/enums/staff-application/evalutation.enum'
import { MaritalStatusEnum } from '../../../../lib/types/enums/staff-application/marital-status.enum'
import { SchoolLevelTaughtEnum } from '../../../../lib/types/enums/staff-application/school-level-taught.enum'
import { StaffInput } from '../../../../lib/types/models/staff/staff-input'
import { NullableTransformer } from '../../../../lib/types/utils'
import { createDateStringSchema } from '../../../../lib/utils/validations/date.validation'
import {
  createEnumArraySchema,
  createEnumSchema
} from '../../../../lib/utils/validations/enum-validation'
import { FILE_VALIDATION } from '../../../../lib/utils/validations/file.validation'
import { ibanValidation } from '../../../../lib/utils/validations/iban-validation'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../../lib/utils/validations/int-array-validation'
import { PHONE_NUMBER_SCHEMA } from '../../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'

export const staffPersonalSpaceFormSchema: (
  language: AvailableLanguages,
  t: TFunction<AvailableLanguages, undefined>
) => ZodType<NullableTransformer<StaffInput>> = (
  language: AvailableLanguages,
  t: TFunction<AvailableLanguages, undefined>
) => {
  return z
    .object({
      picture: FILE_VALIDATION().nullish(),
      phoneNumber: PHONE_NUMBER_SCHEMA(t),
      email: createStringSchema('Email', { isEmail: true, language }),
      address1: createStringSchema(t('staff.personal.space.address'), { language }),
      address2: createStringSchema(t('staff.personal.space.supplementary.address'), {
        nullish: true,
        language
      }),
      city: createStringSchema(t('staff.personal.space.city'), { language }),
      postalCode: createStringSchema(t('staff.personal.space.postal.code'), { language }),
      country: createStringSchema(t('staff.personal.space.country'), { language }),
      academicZone: createEnumSchema({
        fieldName: t('staff.personal.space.academic.zone'),
        enumType: AcademicZoneEnum,
        nullish: true
      }),
      allergies: createStringSchema(t('staff.personal.space.allergies'), {
        nullish: true,
        language
      }),
      maritalStatus: createEnumSchema({
        fieldName: t('staff.personal.space.civil.status'),
        enumType: MaritalStatusEnum,
        nullish: true
      }),
      numberOfChildren: createNumberSchema(t('staff.personal.space.number.of.children'), {
        isInteger: true,
        nullish: true
      }),
      currentSituation: createStringSchema(t('staff.personal.space.current.status'), {
        nullish: true,
        language
      }),
      educationLevel: createEnumSchema({
        fieldName: t('staff.personal.space.education.level'),
        enumType: EducationLevelEnum,
        nullish: true
      }),
      studiedDomain: createStringSchema(t('staff.personal.space.field.of.study'), {
        nullish: true,
        language
      }),
      establishmentName: createStringSchema(t('staff.personal.space.establishment.name'), {
        nullish: true,
        language
      }),
      establishmentAddress: createStringSchema(t('staff.personal.space.establishment.address'), {
        nullish: true,
        language
      }),
      establishmentPostalCode: createStringSchema(
        t('staff.personal.space.establishment.postal.code'),
        { nullish: true, language }
      ),
      establishmentCity: createStringSchema(t('staff.personal.space.establishment.city'), {
        nullish: true,
        language
      }),
      idStatus: createEnumSchema({ fieldName: 'statut', enumType: DocumentStatusEnum }).nullish(),
      idType: createEnumSchema({
        fieldName: 'Type',
        enumType: IDTypeEnum,
        nullish: true
      }),
      idNumber: createStringSchema('N°', { nullish: true, language }),
      idFile: FILE_VALIDATION().nullish(),
      idDeliveredBy: createStringSchema(t('staff.personal.space.issuing.country'), {
        nullish: true,
        language
      }),
      idDeliveredAt: createDateStringSchema({
        inputName: t('staff.personal.space.date.of.issue')
      }).nullish(),
      idValidUntil: createDateStringSchema({
        inputName: t('staff.personal.space.expiration.date')
      }).nullish(),
      criminalRecordStatus: createEnumSchema({
        fieldName: 'statut',
        enumType: DocumentStatusEnum
      }),
      criminalRecord: FILE_VALIDATION().nullish(),
      paySlipStatus: createEnumSchema({
        fieldName: 'statut',
        enumType: DocumentStatusEnum,
        nullish: true
      }),
      paySlip: FILE_VALIDATION().nullish(),
      iban: ibanValidation(language).nullish(),
      bic: createStringSchema('BIC', { nullish: true, language }),
      hasComplemenaryInsurance: z.boolean(),
      partOfTheEuropeanUnion: z.boolean({
        message: t('errors.part.of.the.europian.union')
      }),
      complementaryInsuranceName: createStringSchema(
        t('staff.personal.space.complementary.insurance.name'),
        { nullish: true }
      ),
      emergencyContactName: createStringSchema(t('staff.personal.space.emergency.contact.name'), {
        nullish: true
      }),
      emergencyContactPhoneNumber: PHONE_NUMBER_SCHEMA(t).nullish(),
      emergencyContactRelationship: createStringSchema(
        t('staff.personal.space.emergency.contact.relationship'),
        { nullish: true }
      ),
      hobbyIds: createNumberArraySchema(t('staff.personal.space.hobbies')),

      classLevels: createEnumArraySchema({
        fieldName: t('staff.personal.space.taught.class.level'),
        enumType: SchoolLevelTaughtEnum
      }),
      globalEvaluation: createEnumSchema({ fieldName: 'évaluation', enumType: EvalutationEnum }),
      etaStatus: createEnumSchema({
        fieldName: 'statut',
        enumType: DocumentStatusEnum,
        nullish: true
      }),
      eta: FILE_VALIDATION().nullish(),
      diplomas: z.array(
        z.object({
          status: createEnumSchema({ fieldName: 'statut', enumType: DocumentStatusEnum }),
          type: createEnumSchema({
            fieldName: t('staff.personal.space.diploma'),
            enumType: DiplomaEnum
          }),
          file: FILE_VALIDATION().nullish(),
          diplomaTitle: createStringSchema(t('staff.personal.space.diploma.name'), {
            nullish: true
          })
        })
      )
    })
    .superRefine((data, ctx) => {
      const otherDiplomas = data.diplomas.filter((diploma) => diploma.type === DiplomaEnum.OTHER)
      for (const otherDiploma of otherDiplomas) {
        if (otherDiploma && !otherDiploma.diplomaTitle) {
          const index = data.diplomas.findIndex((diploma) => otherDiploma === diploma)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('errors.diploma.name.required'),
            path: ['diplomas', index, 'otherDiplomaName']
          })
        }
      }
    })
}
